import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Input,
  Row,
  Space,
  Statistic,
  Typography,
  Upload,
} from 'antd';
import { FileTextOutlined, UploadOutlined } from '@ant-design/icons';
import type { CloudDeck } from '@lingoping/core';
import { getCloudDecksApi } from '../services/cloudDecks.js';
import {
  ImportSummary,
  SUPPORTED_IMPORT_EXTENSIONS,
  candidatesToDeckWordsWithMeanings,
  importFromFile,
  importFromPaste,
} from '../services/importService.js';
import { ImportDeckModal, ImportTarget } from './ImportDeckModal.js';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

interface Props {
  targetLanguage?: string;
  /** Called after the words were saved, so other screens can refresh. */
  onChanged: () => void;
  onOpenDeck: (deckId: string) => void;
}

type Stage = 'idle' | 'summary' | 'saved';

interface Saved {
  deckId: string;
  title: string;
  added: number;
  skipped: number;
  followed: boolean;
  /** Words saved as one entry per meaning ("Bank": bench, bank). */
  split: number;
}

/** "Der kleine Prinz" from `der-kleine-prinz.txt`; a short label for pasted text. */
export function suggestDeckName(sourceName: string | null): string {
  if (!sourceName) return 'Imported words';
  const base = sourceName
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : 'Imported words';
}

/**
 * Import words: read a file or pasted text, show what was found, then ask where the words should
 * go. They are only saved as a deck (new, or added to one of the learner's own decks) in the
 * order of the file; nothing is added to the learner's learning until they choose word by word.
 */
export const ImportPanel: React.FC<Props> = ({ targetLanguage = 'de', onChanged, onOpenDeck }) => {
  const [stage, setStage] = useState<Stage>('idle');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pasteText, setPasteText] = useState('');
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [sourceName, setSourceName] = useState<string | null>(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [myDecks, setMyDecks] = useState<CloudDeck[]>([]);
  const [saved, setSaved] = useState<Saved | null>(null);

  useEffect(() => {
    void (async () => {
      const lists = await (await getCloudDecksApi()).readLists();
      setMyDecks(lists.decks.filter((d) => !!d.owner_id));
    })();
  }, [stage]);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setBusy(true);
      try {
        setSummary(await importFromFile(file, targetLanguage));
        setSourceName(file.name);
        setStage('summary');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not read that file.');
      } finally {
        setBusy(false);
      }
    },
    [targetLanguage]
  );

  const handlePaste = useCallback(async () => {
    if (!pasteText.trim()) return;
    setError(null);
    setBusy(true);
    try {
      setSummary(await importFromPaste(pasteText, targetLanguage));
      setSourceName(null);
      setStage('summary');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not parse that text.');
    } finally {
      setBusy(false);
    }
  }, [pasteText, targetLanguage]);

  const uploadProps = {
    accept: SUPPORTED_IMPORT_EXTENSIONS.join(','),
    showUploadList: false,
    beforeUpload: (file: File) => {
      void handleFile(file);
      return false;
    },
  };

  const reset = useCallback(() => {
    setStage('idle');
    setSummary(null);
    setSourceName(null);
    setSaved(null);
    setPasteText('');
    setPromptOpen(false);
  }, []);

  const save = async (target: ImportTarget, follow: boolean) => {
    if (!summary) return;
    setSaving(true);
    setError(null);
    try {
      const api = await getCloudDecksApi();
      const words = await candidatesToDeckWordsWithMeanings(summary.candidates);
      let deck: CloudDeck;
      let added: number;
      let skipped = 0;
      if (target.kind === 'new') {
        deck = await api.createDeck(target.name, words);
        added = deck.word_count;
        skipped = words.length - added;
      } else {
        const result = await api.addWords(target.deckId, words);
        const existing = (await api.readLists()).decks.find((d) => d.id === target.deckId);
        if (!existing) throw new Error('That deck no longer exists.');
        deck = existing;
        ({ added, skipped } = result);
      }
      if (follow) await api.follow(deck.id);
      // Wait for the upload when the server can be reached, so a refused deck (the limit of
      // decks or words) is reported here instead of vanishing later.
      if (await api.flush()) {
        const failures = await api.takeFailures();
        if (failures.length > 0) throw new Error(failures.join(' '));
      }
      const split = new Set(words.filter((w) => w.sense_key || w.sense_hint).map((w) => w.lemma))
        .size;
      setSaved({ deckId: deck.id, title: deck.title, added, skipped, followed: follow, split });
      setPromptOpen(false);
      setStage('saved');
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "The deck couldn't be saved.");
      setPromptOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Import words">
      {error && (
        <Alert
          type="error"
          showIcon
          title={error}
          style={{ marginBottom: 14 }}
          closable={{ onClose: () => setError(null) }}
        />
      )}

      {stage === 'idle' && (
        <Flex vertical gap={14}>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            Save a word list (text, Excel, CSV) or subtitles as a deck.
          </Paragraph>
          <Upload.Dragger {...uploadProps} disabled={busy} style={{ padding: '8px 0' }}>
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">Click or drag a file here</p>
            <p className="ant-upload-hint">Supported: .xlsx, .csv, .tsv, .txt, .srt and .vtt</p>
          </Upload.Dragger>

          <Text type="secondary">Or paste a word list or some German text:</Text>
          <TextArea
            rows={4}
            aria-label="Paste a word list or German text"
            placeholder="One word per line, or any German text…"
            value={pasteText}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setPasteText(e.target.value)}
          />
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            loading={busy}
            disabled={!pasteText.trim()}
            onClick={handlePaste}
            style={{ alignSelf: 'flex-start' }}
          >
            Find words
          </Button>
        </Flex>
      )}

      {stage === 'summary' && summary && (
        <Flex vertical gap={14}>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Statistic title="Words found" value={summary.totalFound} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Repeated" value={summary.duplicates} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Known or learning"
                value={summary.alreadyKnown}
                styles={{ content: { color: '#15803d' } }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="New to you"
                value={summary.newWords}
                styles={{ content: { color: '#6d28d9', fontWeight: 'bold' } }}
              />
            </Col>
          </Row>
          <Text type="secondary" role="status">
            {summary.candidates.length} word{summary.candidates.length === 1 ? '' : 's'} will be
            saved in the deck, {summary.newWords} of them new to you.
            {summary.alreadyKnown > 0 && ' The others stay in it and show their status.'}
          </Text>
          {summary.notWords > 0 && (
            <Text type="secondary">
              {summary.notWords} name{summary.notWords === 1 ? '' : 's'} or non-German word
              {summary.notWords === 1 ? '' : 's'} skipped.
            </Text>
          )}
          {!summary.normalized && summary.totalFound > 0 && (
            <Alert
              type="warning"
              showIcon
              title="Some words couldn't be reduced to their basic form right now, so forms like “neuen” may appear as they are."
            />
          )}
          <Space>
            <Button
              type="primary"
              disabled={summary.candidates.length === 0}
              onClick={() => setPromptOpen(true)}
            >
              Save as a deck
            </Button>
            <Button onClick={reset}>Start over</Button>
          </Space>
          {summary.candidates.length === 0 && (
            <Alert type="info" showIcon title="No words were found to save." />
          )}
          <ImportDeckModal
            open={promptOpen}
            sourceName={sourceName ?? 'your pasted text'}
            suggestedName={suggestDeckName(sourceName)}
            totalWords={summary.candidates.length}
            alreadyKnown={summary.alreadyKnown}
            decks={myDecks}
            saving={saving}
            onCancel={() => setPromptOpen(false)}
            onSave={(target, follow) => void save(target, follow)}
          />
        </Flex>
      )}

      {stage === 'saved' && saved && (
        <Flex vertical gap={14} align="center" style={{ textAlign: 'center', padding: '12px 0' }}>
          <Title level={4} style={{ margin: 0 }}>
            Deck saved
          </Title>
          <Text type="secondary">
            {saved.added} word{saved.added === 1 ? '' : 's'} added to “{saved.title}”
            {saved.skipped > 0 ? `, ${saved.skipped} already there` : ''}.
            {saved.split > 0
              ? ` ${saved.split === 1 ? 'One word has' : `${saved.split} words have`} several meanings and ${saved.split === 1 ? 'is' : 'are'} listed once per meaning.`
              : ''}
            {saved.followed ? ' You follow this deck now.' : ''}
          </Text>
          <Space>
            <Button type="primary" onClick={() => onOpenDeck(saved.deckId)}>
              Open deck
            </Button>
            <Button onClick={reset}>Import more words</Button>
          </Space>
        </Flex>
      )}
    </Card>
  );
};
